// app/javascript/dashboard/routes/dashboard/bulkmessage/utils/useCsvImport.js
import { ref, reactive } from 'vue';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export default function useCsvImport() {
  const parsedCsvRows = ref([]);
  const csvHeaders = ref([]);
  const showMapper = ref(false);
  const wizardStep = ref(0);

  const mapping = reactive({
    phone: '',
    name: '',
    additional_columns: [],
  });

  // Function to guess the most likely columns for phone and name
  const guessColumnsMapping = () => {
    if (
      !csvHeaders.value ||
      csvHeaders.value.length === 0 ||
      !parsedCsvRows.value ||
      parsedCsvRows.value.length === 0
    ) {
      return;
    }

    // More comprehensive keywords list with common variations
    const phoneKeywords = [
      'phone',
      'telefone',
      'telefones',
      'celular',
      'celulares',
      'mobile',
      'tel',
      'contato',
      'contatos',
      'number',
      'telefono',
      'telefonos',
      'whatsapp',
      'wpp',
      'zap',
      'numero',
      'numeros',
      'contact',
      'contacts',
      'fone',
      'fones',
      'phone number',
      'numero telefone',
      'phone no',
      'phone #',
      'tel #',
    ];

    const nameKeywords = [
      'name',
      'names',
      'nome',
      'nomes',
      'contato',
      'contatos',
      'contact',
      'contacts',
      'customer',
      'customers',
      'cliente',
      'clientes',
      'person',
      'people',
      'pessoa',
      'pessoas',
      'usuario',
      'usuarios',
      'user',
      'users',
      'full name',
      'full_name',
      'nome completo',
      'client',
    ];

    // Helper function to score a column header based on keywords
    const scoreColumnHeader = (header, keywords) => {
      // Convert header to string to handle numeric headers
      const lowerHeader = String(header).toLowerCase();
      for (const keyword of keywords) {
        if (lowerHeader.includes(keyword.toLowerCase())) {
          return true;
        }
      }
      return false;
    };

    // Helper function - improved phone detection
    const looksLikePhoneNumber = value => {
      if (!value) return false;
      const valueStr = String(value);

      // Remove all non-numeric characters
      const cleanValue = valueStr.replace(/\D/g, '');

      // If it's mostly digits with a reasonable length, likely a phone number
      return (
        cleanValue.length >= 8 &&
        cleanValue.length <= 15 &&
        cleanValue.length / valueStr.length > 0.5
      ); // At least 50% digits
    };

    // Find columns by header names first
    let bestPhoneColumn = '';
    let bestNameColumn = '';

    // Check all headers for matches
    for (const header of csvHeaders.value) {
      if (!bestPhoneColumn && scoreColumnHeader(header, phoneKeywords)) {
        bestPhoneColumn = header;
      }
      if (!bestNameColumn && scoreColumnHeader(header, nameKeywords)) {
        bestNameColumn = header;
      }
    }

    // If we didn't find by header names, analyze the content
    if (!bestPhoneColumn || !bestNameColumn) {
      // Score each column based on content
      const columnScores = {};

      // Initialize scores
      csvHeaders.value.forEach(header => {
        columnScores[header] = { phoneScore: 0, nameScore: 0 };
      });

      // Check the first few rows to determine column types
      const rowsToAnalyze = Math.min(parsedCsvRows.value.length, 10);

      for (let i = 0; i < rowsToAnalyze; i++) {
        const row = parsedCsvRows.value[i];

        for (const header of csvHeaders.value) {
          const value = row[header];

          if (value) {
            if (looksLikePhoneNumber(value)) {
              columnScores[header].phoneScore += 1;
            }

            // Names typically have more letters than numbers and no special pattern
            if (
              typeof value === 'string' &&
              value.length > 1 &&
              value.replace(/[^a-zA-Z]/g, '').length >
                value.replace(/[^0-9]/g, '').length
            ) {
              columnScores[header].nameScore += 1;
            }
          }
        }
      }

      // Find best column for phone if not already found by header
      if (!bestPhoneColumn) {
        let bestScore = 0;
        for (const header in columnScores) {
          if (columnScores[header].phoneScore > bestScore) {
            bestScore = columnScores[header].phoneScore;
            bestPhoneColumn = header;
          }
        }
      }

      // Find best column for name if not already found by header
      if (!bestNameColumn) {
        let bestScore = 0;
        for (const header in columnScores) {
          // Skip phone column to avoid using the same column for both
          if (header === bestPhoneColumn) continue;

          if (columnScores[header].nameScore > bestScore) {
            bestScore = columnScores[header].nameScore;
            bestNameColumn = header;
          }
        }
      }
    }

    // Set the best guesses in the mapping object
    if (bestPhoneColumn) {
      mapping.phone = bestPhoneColumn;
      console.log(`Auto-detected phone column: ${bestPhoneColumn}`);
    }

    if (bestNameColumn) {
      mapping.name = bestNameColumn;
      console.log(`Auto-detected name column: ${bestNameColumn}`);
    }
  };

  const handleFileUpload = (event, onSuccess) => {
    const file = event.target.files[0];
    if (!file) return;

    // Read and parse the file
    const reader = new FileReader();
    reader.onload = () => {
      const fileType = file.name.split('.').pop().toLowerCase();

      if (fileType === 'csv') {
        const csvData = reader.result;
        
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: results => {
            
            // Store columns and raw rows
            csvHeaders.value = results.meta.fields;
            parsedCsvRows.value = results.data;

            // Apply auto-detection of columns
            guessColumnsMapping();

            // Start the wizard at first step
            wizardStep.value = 0;
            // Open mapping modal
            showMapper.value = true;

            if (onSuccess) onSuccess();

            // Reset file input
            event.target.value = null;
          },
          error: error => {
            console.error('❌ Error parsing CSV:', error);
            event.target.value = null;
          },
        });
      } else if (['xlsx', 'xls'].includes(fileType)) {
        try {
          const data = new Uint8Array(reader.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert worksheet to JSON with headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Extract headers (first row) and data
          const headers = jsonData[0];
          const rows = jsonData.slice(1).map(row => {
            // Create object with header keys
            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = row[index] || ''; // Handle undefined values
            });
            return rowData;
          });

          csvHeaders.value = headers;
          parsedCsvRows.value = rows;

          // Apply auto-detection of columns
          guessColumnsMapping();

          // Open mapping wizard
          wizardStep.value = 0;
          showMapper.value = true;

          if (onSuccess) onSuccess();

          // Reset file input
          event.target.value = null;
        } catch (error) {
          console.error('❌ Error processing Excel file:', error);
          console.error('  Stack trace:', error.stack);
          event.target.value = null;
        }
      }
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const processImportedData = (data, headers) => {
    // Store columns and raw rows
    csvHeaders.value = headers;
    parsedCsvRows.value = data;

    // Apply auto-detection of columns
    guessColumnsMapping();

    // Open mapping wizard
    wizardStep.value = 0;
    showMapper.value = true;
  };

  const nextWizardStep = () => {
    if (wizardStep.value < 1) {
      wizardStep.value++;
    }
  };

  const previousWizardStep = () => {
    if (wizardStep.value > 0) {
      wizardStep.value--;
    }
  };

  const skipStep = () => {
    if (wizardStep.value === 1) {
      mapping.name = '';
    }
    nextWizardStep();
  };

  const cancelMapping = () => {
    showMapper.value = false;
    wizardStep.value = 0;
    mapping.phone = '';
    mapping.name = '';
    mapping.additional_columns = [];
    parsedCsvRows.value = [];
    csvHeaders.value = [];
  };

  return {
    parsedCsvRows,
    csvHeaders,
    showMapper,
    wizardStep,
    mapping,
    guessColumnsMapping,
    handleFileUpload,
    processImportedData,
    nextWizardStep,
    previousWizardStep,
    skipStep,
    cancelMapping,
  };
}
